import { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { MapPin, Plus, ChartBar, FunnelSimple } from "@phosphor-icons/react";

/**
 * Rent in your area — user-submitted comps.
 * Shows median / p25 / p75 + recent samples for a pincode.
 * "Add yours" form contributes to the dataset (auto-stamped with date + location).
 */
export default function RentComps() {
  const [pincode, setPincode] = useState("");
  const [propertyType, setPropertyType] = useState(""); // "" | "apartment" | "individual_house"
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);

  const search = async (e) => {
    e?.preventDefault?.();
    if (!/^\d{6}$/.test(pincode.trim())) {
      toast.error("Enter a valid 6-digit pincode");
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ pincode: pincode.trim() });
      if (propertyType) params.set("property_type", propertyType);
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      const { data } = await api.get(`/rent-comps?${params.toString()}`);
      setStats(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6 sm:py-12" data-testid="rent-comps-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="eyebrow mb-2">Rent comps</div>
          <h1 className="font-serif text-3xl sm:text-5xl">What's the going rate near you?</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Real numbers from real Estima users. Every submission is anonymous and stamped with the date you add it.
          </p>
        </div>
        <button onClick={() => setSubmitOpen(true)} className="btn-primary px-5 py-3 text-sm inline-flex items-center gap-2" data-testid="rent-comps-add-btn">
          <Plus size={14} weight="bold" /> Add yours
        </button>
      </div>

      <form onSubmit={search} className="card-flat p-5 mb-8 space-y-3">
        <div className="flex gap-3 items-end flex-wrap">
          <label className="flex-1 min-w-[180px]">
            <span className="eyebrow block mb-1">6-digit pincode</span>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={pincode}
              onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
              placeholder="411014"
              className="input-dark w-full px-3 py-2.5 text-base"
              data-testid="rent-comps-pincode-input"
            />
          </label>
          <label className="min-w-[180px]">
            <span className="eyebrow block mb-1">Property type</span>
            <select
              value={propertyType}
              onChange={(e) => setPropertyType(e.target.value)}
              className="input-dark w-full px-3 py-2.5 text-sm"
              data-testid="rent-comps-property-type"
            >
              <option value="">Any</option>
              <option value="apartment">Apartment / Flat</option>
              <option value="individual_house">Individual house</option>
            </select>
          </label>
          <button type="submit" disabled={loading || pincode.length !== 6} className="btn-primary px-5 py-2.5 text-sm" data-testid="rent-comps-search-btn">
            {loading ? "Searching…" : "Show rents"}
          </button>
        </div>
        <div className="flex gap-3 items-end flex-wrap text-xs">
          <FunnelSimple size={14} weight="duotone" className="text-muted-foreground self-center" />
          <label>
            <span className="eyebrow block mb-1">From date</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="input-dark px-3 py-2 text-xs"
              data-testid="rent-comps-from-date"
            />
          </label>
          <label>
            <span className="eyebrow block mb-1">To date</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="input-dark px-3 py-2 text-xs"
              data-testid="rent-comps-to-date"
            />
          </label>
          {(fromDate || toDate || propertyType) && (
            <button
              type="button"
              onClick={() => { setFromDate(""); setToDate(""); setPropertyType(""); }}
              className="btn-ghost px-3 py-2 text-xs"
              data-testid="rent-comps-clear-filters"
            >
              Clear filters
            </button>
          )}
        </div>
      </form>

      {stats && (
        <div data-testid="rent-comps-stats">
          {stats.count === 0 ? (
            <div className="card-flat p-12 text-center">
              <MapPin size={28} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No data for this pincode yet. Be the first — click "Add yours".</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
                <Kpi label="Median rent" value={`₹${Number(stats.median_rent).toLocaleString("en-IN")}/mo`} big tone="secondary" />
                <Kpi label="25th–75th percentile" value={`₹${Number(stats.p25_rent).toLocaleString("en-IN")} – ₹${Number(stats.p75_rent).toLocaleString("en-IN")}`} />
                <Kpi label="Avg ₹/sqft" value={`₹${stats.avg_rent_per_sqft}`} />
                <Kpi label="Submissions" value={String(stats.count)} />
              </div>

              <div className="card-flat p-6">
                <div className="eyebrow mb-3 flex items-center gap-2"><ChartBar size={14} weight="duotone" /> Recent samples</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="rent-comps-samples-table">
                    <thead>
                      <tr className="border-b hairline text-muted-foreground uppercase text-[10px] tracking-wider">
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Society / Area</th>
                        <th className="text-left p-2">Type</th>
                        <th className="text-right p-2">BHK</th>
                        <th className="text-right p-2">Sqft</th>
                        <th className="text-right p-2">Rent / month</th>
                        <th className="text-right p-2">₹/sqft</th>
                        <th className="text-left p-2">Furnished</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.samples.map((s) => (
                        <tr key={s.id} className="border-b hairline last:border-0">
                          <td className="p-2 text-xs text-muted-foreground whitespace-nowrap">
                            {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="p-2">
                            <div>{s.society_name || s.locality || "—"}</div>
                            {s.area_name && <div className="text-[10px] text-muted-foreground">{s.area_name}</div>}
                          </td>
                          <td className="p-2 text-xs capitalize">
                            {(s.property_type || "apartment").replace("_", " ")}
                          </td>
                          <td className="p-2 text-right">{s.bhk}</td>
                          <td className="p-2 text-right">{Number(s.area_sqft).toLocaleString("en-IN")}</td>
                          <td className="p-2 text-right">₹{Number(s.monthly_rent).toLocaleString("en-IN")}</td>
                          <td className="p-2 text-right">₹{s.rent_per_sqft}</td>
                          <td className="p-2 text-xs text-muted-foreground">{s.is_furnished ? "yes" : "no"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {submitOpen && <SubmitCompModal onClose={() => setSubmitOpen(false)} onSuccess={() => { setSubmitOpen(false); search(); }} prefillPincode={pincode} />}
    </div>
  );
}

function SubmitCompModal({ onClose, onSuccess, prefillPincode }) {
  const [form, setForm] = useState({
    pincode: prefillPincode || "",
    city: "",
    locality: "",
    society_name: "",
    area_name: "",
    property_type: "apartment",
    bhk: 2,
    area_sqft: 900,
    monthly_rent: 25000,
    is_furnished: false,
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(form.pincode)) { toast.error("6-digit pincode"); return; }
    if (!form.city.trim()) { toast.error("City required"); return; }
    setSaving(true);
    try {
      await api.post("/rent-comps", form);
      toast.success("Added — thanks for sharing");
      onSuccess();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Login required to submit");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[10010] flex items-center justify-center p-4" data-testid="rent-comps-modal">
      <button className="absolute inset-0 bg-black/70" onClick={onClose} aria-label="Close" />
      <form onSubmit={submit} className="relative card-flat w-full max-w-lg p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="eyebrow">Add a rent comp</div>
        <p className="text-[11px] text-muted-foreground -mt-2">
          We auto-stamp today's date so the data stays trackable over time.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Pincode" v={form.pincode} onChange={(v) => setForm({ ...form, pincode: v.replace(/\D/g, "").slice(0, 6) })} tid="rc-pincode" />
          <Field label="City" v={form.city} onChange={(v) => setForm({ ...form, city: v })} tid="rc-city" />
          <Field label="Society name" v={form.society_name} onChange={(v) => setForm({ ...form, society_name: v })} tid="rc-society" />
          <Field label="Area name" v={form.area_name} onChange={(v) => setForm({ ...form, area_name: v })} tid="rc-area" />
          <label className="text-xs col-span-2">
            <span className="eyebrow block mb-1">Property type</span>
            <select
              value={form.property_type}
              onChange={(e) => setForm({ ...form, property_type: e.target.value })}
              className="input-dark w-full px-3 py-2 text-sm"
              data-testid="rc-property-type"
            >
              <option value="apartment">Apartment / Flat</option>
              <option value="individual_house">Individual house</option>
            </select>
          </label>
          <NumF label="BHK" v={form.bhk} onChange={(v) => setForm({ ...form, bhk: v })} tid="rc-bhk" />
          <NumF label="Area sqft" v={form.area_sqft} onChange={(v) => setForm({ ...form, area_sqft: v })} tid="rc-sqft" />
          <NumF label="Monthly rent ₹" v={form.monthly_rent} onChange={(v) => setForm({ ...form, monthly_rent: v })} tid="rc-rent" />
          <Field label="Locality (optional)" v={form.locality} onChange={(v) => setForm({ ...form, locality: v })} tid="rc-locality" />
        </div>
        <label className="text-xs flex items-center gap-2">
          <input type="checkbox" checked={form.is_furnished} onChange={(e) => setForm({ ...form, is_furnished: e.target.checked })} data-testid="rc-furnished" />
          Furnished
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary px-4 py-2 text-sm" data-testid="rc-submit">
            {saving ? "Saving…" : "Submit"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, v, onChange, tid }) {
  return (
    <label className="text-xs">
      <span className="eyebrow block mb-1">{label}</span>
      <input value={v} onChange={(e) => onChange(e.target.value)} className="input-dark w-full px-3 py-2 text-sm" data-testid={tid} />
    </label>
  );
}
function NumF({ label, v, onChange, tid }) {
  return (
    <label className="text-xs">
      <span className="eyebrow block mb-1">{label}</span>
      <input type="number" value={v} onChange={(e) => onChange(Number(e.target.value || 0))} className="input-dark w-full px-3 py-2 text-sm" data-testid={tid} />
    </label>
  );
}
function Kpi({ label, value, big, tone }) {
  const toneClass = tone === "secondary" ? "text-[hsl(var(--secondary))]" : "text-foreground";
  return (
    <div className="card-flat p-4">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
      <div className={`${big ? "num-metric text-2xl" : "num-metric text-base"} ${toneClass}`}>{value}</div>
    </div>
  );
}
